import { addMinutes } from 'date-fns';
import { useCallback } from 'react';
import { useIntl } from 'react-intl';

import type { EventAttributes, SourceType } from '@trello/analytics-types';
import { Analytics, formatContainers } from '@trello/atlassian-analytics';
import { useMemberId } from '@trello/authentication';
import { getTrelloCardDataFromCardId } from '@trello/business-logic-react/card';
import { sendErrorEvent } from '@trello/error-reporting';
import { client } from '@trello/graphql';
import { showFlag } from '@trello/nachos/experimental-flags';
import { getSmartCardClient } from '@trello/smart-card/smart-card-client';

import {
  DND_NEW_EVENT_DURATION_IN_MINUTES,
  DND_TEMP_EVENT_ID_PREFIX,
} from './constants';
import { getIsInboxBoard } from './getIsInboxBoard';
import type { TrelloPlannerCalendarNode } from './Planner.types';
import {
  PlannerCardDataFragmentDoc,
  type PlannerCardDataFragment,
} from './PlannerCardDataFragment.generated';
import { useTrelloCreateCalendarEventMutation } from './TrelloCreateCalendarEventMutation.generated';
import { useCalendarRef } from './useCalendarRef';
import { usePlannerAccount } from './usePlannerAccount';
import { useToggleCalendar } from './useToggleCalendar';
import { useWorkspaceAri } from './useWorkspaceAri';

type CreatePlannerEventArgs = {
  startDate?: Date | null;
  endDate?: Date | null;
  eventCardId: string;
};

const taskName = 'create-plannerEvent';

export const useCreatePlannerEvent = ({
  source,
  optimisticEventPrefix,
}: {
  source: SourceType;
  optimisticEventPrefix: string;
}) => {
  const intl = useIntl();
  const memberId = useMemberId();
  const { calendarRef } = useCalendarRef();
  const calendarApi = calendarRef.current?.getApi();

  const { workspaceId } = useWorkspaceAri();
  const { plannerAccountId, enabledCalendars, plannerAccountDisplayName } =
    usePlannerAccount();

  const { toggleCalendarMutation, toggledCalendarId } = useToggleCalendar();
  const [createCalendarEvent] = useTrelloCreateCalendarEventMutation();

  const isDndEvent = DND_TEMP_EVENT_ID_PREFIX === optimisticEventPrefix;

  return useCallback(
    async ({
      startDate,
      endDate,
      eventCardId,
    }: CreatePlannerEventArgs): Promise<void> => {
      let primaryCalendarId = enabledCalendars?.find(
        (calendar: TrelloPlannerCalendarNode) => calendar?.isPrimary,
      )?.id;

      const shouldTogglePrimaryCalendar = Boolean(!primaryCalendarId);

      if (!plannerAccountId || !startDate || !eventCardId) {
        return;
      }

      const traceId = Analytics.startTask({
        taskName,
        source,
      });

      const cardData = client.readFragment<PlannerCardDataFragment>({
        id: client.cache.identify({
          __typename: 'Card',
          id: eventCardId,
        }),
        fragment: PlannerCardDataFragmentDoc,
      });
      const boardId = cardData?.idBoard || '';
      const isInboxBoard = getIsInboxBoard(memberId, boardId);

      const uiEventAttributes: EventAttributes = {
        personalProductivity: 'planner',
        containerType: 'planner',
        cardId: eventCardId,
        source: isInboxBoard ? 'inbox' : 'board',
        action: 'schedule',
        taskId: traceId,
      };

      if (isDndEvent) {
        Analytics.sendUIEvent({
          action: 'dropped',
          actionSubject: 'card',
          source,
          attributes: uiEventAttributes,
          containers: formatContainers({ workspaceId }),
        });
      } else {
        Analytics.sendClickedButtonEvent({
          buttonName: 'addPlannerEventFromCardSaveButton',
          source,
          attributes: uiEventAttributes,
          containers: formatContainers({ workspaceId }),
        });
      }

      try {
        if (shouldTogglePrimaryCalendar && plannerAccountDisplayName) {
          await toggleCalendarMutation({
            providerCalendarId: plannerAccountDisplayName,
            start: calendarApi?.view.currentStart.toISOString() || '',
            end: calendarApi?.view.currentEnd.toISOString() || '',
            newCheckedState: shouldTogglePrimaryCalendar,
            traceId,
          });
          primaryCalendarId = toggledCalendarId.current;
        }

        const { cardAri, cardName, cardRole, cardObjectId } =
          getTrelloCardDataFromCardId(eventCardId);
        let focusEventName = cardName;
        const smartCardClient = getSmartCardClient();
        if (cardRole === 'link') {
          const fetchLinkCardName = async () => {
            const resolved = await smartCardClient
              .fetchData(cardName || '')
              .catch(() => {});
            if (resolved?.data && 'name' in resolved.data) {
              focusEventName = resolved.data.name || '';
            }
          };
          await fetchLinkCardName();
        }

        if (!primaryCalendarId) {
          return;
        }

        const eventStart = startDate.toISOString();
        const eventEnd = endDate
          ? endDate.toISOString()
          : addMinutes(
              startDate,
              DND_NEW_EVENT_DURATION_IN_MINUTES,
            ).toISOString();

        await createCalendarEvent({
          variables: {
            start: calendarApi?.view.currentStart.toISOString() || '',
            end: calendarApi?.view.currentEnd.toISOString() || '',
            plannerCalendarId: primaryCalendarId,
            providerAccountId: plannerAccountId,
            event: {
              title:
                focusEventName ??
                intl.formatMessage({
                  id: 'templates.planner.focus-time',
                  defaultMessage: 'Focus time',
                  description:
                    'Default title for new planner events when a fallback is needed',
                }),
              cardId: cardAri,
              start: eventStart,
              end: eventEnd,
            },
          },
          context: {
            traceId,
          },
          optimisticResponse: {
            __typename: 'Mutation',
            trello: {
              __typename: 'TrelloMutationApi',
              createPlannerCalendarEvent: {
                __typename: 'TrelloCreatePlannerCalendarEventPayload',
                success: true,
                errors: null,
                plannerCalendarUpdated: {
                  __typename: 'TrelloPlannerCalendar',
                  id: primaryCalendarId,
                  events: {
                    __typename: 'TrelloPlannerCalendarEventConnection',
                    edges: [
                      {
                        __typename: 'TrelloPlannerCalendarEventEdge',
                        node: {
                          __typename: 'TrelloPlannerCalendarEvent',
                          id: `${optimisticEventPrefix}${eventCardId}-${Date.now().toString(36)}`,
                          title: focusEventName,
                          startAt: eventStart,
                          endAt: eventEnd,
                          allDay: false,
                          cards: {
                            __typename:
                              'TrelloPlannerCalendarEventCardConnection',
                            edges: [
                              {
                                __typename:
                                  'TrelloPlannerCalendarEventCardEdge',
                                node: {
                                  __typename: 'TrelloPlannerCalendarEventCard',
                                  id: cardAri,
                                  card: {
                                    __typename: 'TrelloCard',
                                    id: cardAri,
                                    objectId: cardObjectId,
                                    name: cardName,
                                  },
                                  position: 0,
                                },
                              },
                            ],
                          },
                          color: 'PURPLE_SUBTLEST',
                          conferencing: null,
                          eventType: 'PLANNER_EVENT',
                          link: null,
                          status: 'ACCEPTED',
                        },
                      },
                    ],
                  },
                },
              },
            },
          },
        });

        Analytics.sendTrackEvent({
          action: 'created',
          actionSubject: 'plannerEvent',
          source,
          attributes: {
            personalProductivity: 'planner',
            containerType: 'planner',
            method: isDndEvent ? 'dnd' : 'cardQuickActions',
            taskId: traceId,
          },
          containers: formatContainers({
            workspaceId,
          }),
        });

        Analytics.taskSucceeded({ taskName, source, traceId });
      } catch (error) {
        Analytics.taskFailed({ taskName, source, traceId, error });

        const tempEvent = calendarApi?.getEventById(
          `${optimisticEventPrefix}${eventCardId}`,
        );

        if (tempEvent) {
          try {
            tempEvent.remove();
          } catch (removeError) {
            sendErrorEvent(removeError, {
              tags: {
                ownershipArea: 'trello-electric',
                feature: 'Planner',
              },
              extraData: {
                component: isDndEvent
                  ? 'useHandleDrop'
                  : 'AddCardToPlannerPopover',
              },
            });

            throw removeError;
          }
        }

        showFlag({
          id: 'plannerCreateEventError',
          title: intl.formatMessage({
            id: 'templates.planner.add-card-to-event-error',
            defaultMessage:
              'We weren’t able to add an event for this card, please try again',
            description: 'Error flag message for creating a calendar event',
          }),
          appearance: 'error',
          isAutoDismiss: true,
        });
      }
    },
    [
      calendarApi,
      createCalendarEvent,
      enabledCalendars,
      intl,
      isDndEvent,
      memberId,
      optimisticEventPrefix,
      plannerAccountDisplayName,
      plannerAccountId,
      source,
      toggleCalendarMutation,
      toggledCalendarId,
      workspaceId,
    ],
  );
};
